import { get, isEmpty } from "lodash";
import { rApp, rSavedSpreadsheets } from "app/utils/recoil";
import { useRecoilState, useRecoilValue } from "recoil";

import AdminForm from "../components/AdminForm";
import SettingsSection from "./SettingsSection";
import { apiRequest } from "app/utils/apiRequests";
import { getConditionLabel } from "../components/DisplayConditions";
import { getHighest } from "app/utils/utils";
import { successNotification } from "app/utils/Notification";
import { useState } from "react";

const CustomVariables = () => {
  const [app, setApp] = useRecoilState(rApp);

  const [changes, setChanges] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  const dataSources = useRecoilValue(rSavedSpreadsheets);

  const customVariables = get(app, "custom_variables", []) || [];

  const save = () => {
    setIsSaving(true);
    apiRequest.post("/app_settings/", changes).then((r) => {
      setIsSaving(false);
      successNotification("Saved");
      setChanges({});
    });
  };

  const newVarId = getHighest(customVariables, "id");

  const [activeVariableIndex, setActiveVariableIndex] = useState(null);
  const [activeConditionIndex, setActiveConditionIndex] = useState(null);

  const activeVariable = get(customVariables, activeVariableIndex, null);

  const condition = get(
    get(activeVariable, "conditions", []),
    activeConditionIndex
  );

  const conditionKey = get(condition, "key");

  const activeDataSource = dataSources.find(
    (ds) => ds.id === activeVariable?.spreadsheet
  );

  const fieldConfig = get(activeDataSource, "field_data.config", {});

  const conditionFieldConfig = get(fieldConfig, conditionKey);

  let fields = [
    {
      id: "custom_variables",
      label: "Custom Variables",
      componentId: "MultiForm",
      labelSingular: "Variable",
      itemClick: (i) => setActiveVariableIndex(i),
      width: "300px",
      value: customVariables.map((v) => ({ ...v, label: get(v, "key") })),
      newObject: {
        key: `custom_variable${newVarId > 0 ? `_${newVarId}` : ""}`,
      },
      fields: [
        {
          key: "type",
          label: "Variable Type",
          componentId: "Select",
          hideEmptyItem: true,
          defaultValue: "exists",
          hint: "The type of value you want from the matching record",
          options: [
            {
              label: "Record exists (true/false)",
              value: "exists",
            },
            {
              label: "Get value from record",
              value: "recordField",
            },
          ],
        },
        {
          key: "sorting",
          label: "Priority",
          hint: "Determine how you want to prioritize records when there are more than one",
          componentId: "Select",
          defaultValue: "first",
          hideEmptyItem: true,
          options: [
            {
              label: "First Record",
              value: "first",
            },
            {
              label: "Last Record",
              value: "last",
            },
          ],
        },
        {
          key: "sortingColumn",
          label: "Sorting Column",
          hint: "Determine how you want to sort records when there are more than one",
          displayCondition: (f) => f.type === "recordField",
          componentId: "SpreadsheetColumnSelect",
          linkedSpreadsheet: activeDataSource,
        },
        {
          key: "key",
          label: "Variable Key",
          componentId: "Input",
          hint: "The key you will reference this variable by, like {{ custom.your_key }}",
        },
        {
          key: "spreadsheet",
          label: "Spreadsheet",
          componentId: "SpreadsheetSelect",
          hint: "The spreadsheet to get the variable from",
        },
        {
          key: "conditions",
          label: "Conditions",
          componentId: "MultiForm",
          labelSingular: "Condition",
          itemClick: (i) => setActiveConditionIndex(i),
          getItemLabel: (item) =>
            getConditionLabel({
              value1: item.key,
              operator: item.operator,
              value2: item.value,
            }),
          width: "240px",
          value: [],
          newObject: {},
          fields: [
            {
              key: "key",
              label: "Field",
              hint: "Which column to use for matching the row",
              componentId: "SpreadsheetColumnSelect",
              linkedSpreadsheet: activeDataSource,
            },
            {
              key: "operator",
              label: "Operator",
              componentId: "OperatorSelect",
              defaultValue: "equals",
              fieldConfig: conditionFieldConfig,
            },
            {
              key: "value",
              label: "Value",
              hint: "The value to use in the row matchign condition",
              componentId: "DynamicString",
            },
          ],
        },
        {
          key: "recordField",
          label: "Record Field (Optional)",
          hint: "The field you want to display in the variable. If left blank, the entire record will be used and you can access fields like {{ custom.my_variable.field_name }} ",
          displayCondition: (f) => f.type === "recordField",
          componentId: "SpreadsheetColumnSelect",
          linkedSpreadsheet: activeDataSource,
        },
      ],
    },
  ];

  return (
    <SettingsSection
      title="Custom Variables"
      description="Custom variables can be used for more complex situations"
      settingsCategory={"App Settings"}
    >
      <AdminForm
        labelStyle="headingSm"
        sectionPadding="5px"
        fields={fields}
        submitText={"Save Changes"}
        submit={!isEmpty(changes) && save}
        width="220px"
        isFetching={isSaving}
        onChange={(k, v) => {
          setChanges({ ...changes, [k]: v });
          setApp({
            [k]: v,
          });
        }}
      />
    </SettingsSection>
  );
};

export default CustomVariables;
